let unpatched = false;
let patched = false;
let guardDisabled = false;
let styleElement: HTMLStyleElement | null = null;
let translateLocked = false;

type AnyNode = Node & { __gtPatched?: boolean };

const isTranslateOn = () => {
    const html = document.documentElement;
    const lang = html.getAttribute("lang") || "";
    return (
        html.classList.contains("translated-ltr") ||
        html.classList.contains("translated-rtl") ||
        (lang && lang !== "en")
    );
};

export const configureGTGuard = ({ disableGuard, lockTranslate }: { disableGuard: boolean; lockTranslate: boolean }) => {
    guardDisabled = disableGuard;
    translateLocked = lockTranslate;
    const html = typeof document !== "undefined" ? document.documentElement : null;
    if (html) {
        if (translateLocked) html.setAttribute("translate", "no");
        else html.removeAttribute("translate");
    }
    if (guardDisabled) removeGTPatches();
    if (guardDisabled && styleElement) {
        styleElement.remove();
        styleElement = null;
    }
};

let origRemoveChild: typeof Node.prototype.removeChild | null = null;
let origInsertBefore: typeof Node.prototype.insertBefore | null = null;

function safeRemoveChild(this: Node, child: Node) {
    if (!child || child.parentNode !== this) return child;
    try {
        return (origRemoveChild as any).call(this, child);
    } catch (err: any) {
        if (err?.name === "NotFoundError" || err?.name === "HierarchyRequestError") {
            return child;
        }
        throw err;
    }
}

function safeInsertBefore(this: Node, newNode: Node, referenceNode: Node | null) {
    if (referenceNode && referenceNode.parentNode !== this) {
        referenceNode = null;
    }
    try {
        return (origInsertBefore as any).call(this, newNode, referenceNode);
    } catch (err: any) {
        if (err?.name === "NotFoundError" || err?.name === "HierarchyRequestError") {
            return (origInsertBefore as any).call(this, newNode, null);
        }
        throw err;
    }
}

export function applyGTPatches() {
    if (patched || guardDisabled) return;
    origRemoveChild = Node.prototype.removeChild;
    origInsertBefore = Node.prototype.insertBefore;
    Node.prototype.removeChild = safeRemoveChild as any;
    Node.prototype.insertBefore = safeInsertBefore as any;
    patched = true;
    unpatched = false;
}

export function removeGTPatches() {
    if (!patched || unpatched) return;
    if (origRemoveChild) Node.prototype.removeChild = origRemoveChild;
    if (origInsertBefore) Node.prototype.insertBefore = origInsertBefore;
    origRemoveChild = null;
    origInsertBefore = null;
    patched = false;
    unpatched = true;
}

export function initGTGuard() {
    if (guardDisabled) return () => { };
    if (isTranslateOn()) applyGTPatches();

    const html = document.documentElement;
    const check = () => {
        if (guardDisabled) {
            removeGTPatches();
            return;
        }
        if (isTranslateOn()) applyGTPatches();
        else removeGTPatches();
    };
    const obs = new MutationObserver(check);
    obs.observe(html, { attributes: true, attributeFilter: ["class", "lang"] });

    window.addEventListener("popstate", check);
    window.addEventListener("hashchange", check);

    const ensureStyle = () => {
        if (guardDisabled || styleElement) return;
        styleElement = document.createElement("style");
        styleElement.setAttribute("data-gt-guard", "true");
        styleElement.appendChild(
            document.createTextNode("html.translated-ltr font, html.translated-rtl font { pointer-events: none; }")
        );
        document.head.appendChild(styleElement);
    };

    ensureStyle();

    return () => {
        obs.disconnect();
        window.removeEventListener("popstate", check);
        window.removeEventListener("hashchange", check);
        removeGTPatches();
        styleElement?.remove();
        styleElement = null;
    };
}


